import { PropsWithChildren } from 'react';

import { Box, chakra, Flex, Heading, Skeleton, Text, useRadio, useRadioGroup, UseRadioProps } from '@chakra-ui/react';

import { BillingFrequency } from '~/features/checkout/models/checkout';

export function PricePlanRadio({ children, ...props }: PropsWithChildren<UseRadioProps>) {
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } = useRadio(props);

  return (
    <chakra.label {...htmlProps} cursor='pointer' flex='1'>
      <input {...getInputProps({})} aria-label={props.value} />
      <Box
        {...getRadioProps()}
        bg={state.isChecked ? 'supportBlue.20' : 'white'}
        borderRadius='4px'
        p={1}
        w={12}
        width='full'
        {...(state.isFocused && {
          outline: '1px solid',
          outlineColor: 'blue.500',
          outlineOffset: '2px',
        })}
        padding={3}
        position='relative'
        sx={{
          '&::before': {
            content: '""',
            bgColor: state.isChecked ? 'supportBlue.70' : 'supportBlue.20',
            borderRadius: '6px',
            borderWidth: '4px',
            inset: 0,
            position: 'absolute',
            zIndex: -1,
            margin: '-2px',
          },
        }}
      >
        <Flex {...getLabelProps()} color={state.isChecked ? 'black' : 'gray.600'} flexDirection='column' gap={1}>
          {children}
        </Flex>
      </Box>
    </chakra.label>
  );
}

export type PricePlanRadioGroupProps = {
  items: Array<{
    label: string;
    displayPrice?: string;
    displayPriceUnits?: string;
    value: BillingFrequency;
  }>;
  onChange: (value: BillingFrequency) => void;
  value: BillingFrequency;
};

export function PricePlanRadioGroup({ items, onChange, value }: PricePlanRadioGroupProps) {
  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: 'all',
    onChange,
    value,
  });
  return (
    <Flex {...getRootProps()} flexWrap='wrap' gap={3}>
      {items.map((item) => {
        return (
          <PricePlanRadio key={item.value} {...getRadioProps({ value: item.value })}>
            <Heading
              as={Text}
              fontFamily='body'
              fontSize='sm'
              fontWeight='600'
              textTransform='capitalize'
              whiteSpace='nowrap'
            >
              {item.label}
            </Heading>
            <Text color='supportGray.70' fontFamily='body' whiteSpace='nowrap'>
              {item.displayPrice ? (
                <Box
                  as='span'
                  color='supportGray.100'
                  fontSize='1.5rem'
                  fontWeight='600 !important'
                  lineHeight='1.75rem'
                >
                  {item.displayPrice}
                </Box>
              ) : (
                <Skeleton as='span' display='inline-block' height='29.5px' marginEnd={1} width='36px' />
              )}
              {item.displayPriceUnits ? (
                <Box as='span' fontSize='sm' fontWeight='400 !important'>
                  {item.displayPriceUnits}
                </Box>
              ) : (
                <Skeleton as='span' display='inline-block' height='16.5px' width='55px' />
              )}
            </Text>
          </PricePlanRadio>
        );
      })}
    </Flex>
  );
}
